import { Button, Drawer, Flex, Form, Input, Upload, UploadProps } from 'antd';
import { FC, useState } from 'react';
import { useIntl } from 'react-intl';
import { useMessage } from 'shared/libs/hooks/useMessage/useMessage';
import type { UploadRequestOption } from 'rc-upload/lib/interface';
import { BugOutlined } from '@ant-design/icons';
import { general } from 'shared/model/schemas/rules';
import { useDispatch, useSelector } from 'react-redux';
import { getUserMeta } from 'shared/config/store/reducers/userSlice';
import { setActivity } from 'shared/config/store/reducers/appSlice';
import { useReportBugMutation } from '../api';
import { ApiError } from 'shared/model/types';
import { convertBytesToMb, getOSInformation } from 'shared/libs/utils/helpers';

type Props = {
  open: boolean;
  onClose: () => unknown;
}

const BugReportDrawer: FC<Props> = ({ open, onClose }) => {
  const { formatMessage: t } = useIntl();
  const [form] = Form.useForm();
  const [attachments, setAttachments] = useState<any[]>([]);
  const { showError, showSuccess } = useMessage();
  const userInfo = useSelector(getUserMeta);
  const dispatch = useDispatch();
  const [reportBug] = useReportBugMutation();
  const formRules = {
    subject: [general(t)['required-field']],
    description: [general(t)['required-field']]
  }

  const dummyRequest = (options: UploadRequestOption<any>) => {
    setTimeout(() => {
      if (options.onSuccess !== undefined)
        options.onSuccess('ok');
    }, 0);
  };

  const props: UploadProps = {
    name: 'file',
    multiple: true,
    maxCount: 3,
    id: 'bug-report-upload',
    fileList: attachments,
    onChange(info) {
      const { status } = info.file;
      if (status === 'done' || status === 'removed' || status === 'uploading') {
        let size: number = 0;
        info.fileList.forEach((file) => (size += file.size ?? 0));
        const mbSize = convertBytesToMb(size);

        if (mbSize > 5) {
          showError(t({ id: 'screens.settings.bug.report.fileSizeValidationError' }));
        } else {
          setAttachments(info.fileList);
        }
      }
    },
    listType: 'picture',
    onDrop(e) {
      console.log('Dropped files', e.dataTransfer.files);
    },
    customRequest: (options: UploadRequestOption) => dummyRequest(options),
    beforeUpload: (file) => {
      const isImg =
        file.type === 'image/png' ||
        file.type === 'image/jpg' ||
        file.type === 'image/jpeg' ||
        file.type === 'application/pdf';
      if (!isImg) {
        showError(t({ id: 'screens.settings.bug.report.fileSizeValidationError' }));
      }
      return isImg || Upload.LIST_IGNORE;
    },
  };

  const onSubmit = (data: any) => {
    const osInfo = getOSInformation();
    const device =
      'os: ' +
      osInfo.os +
      '\n' +
      'browser: ' +
      osInfo.browser +
      '\n' +
      'browser_version: ' +
      osInfo.browser_version +
      '\n' +
      'login_method: password' +
      '\n';

    const bugReportForm = new FormData();
    bugReportForm.append('email', userInfo.email ?? 'No email');
    bugReportForm.append('subject', data.subject);
    bugReportForm.append('description', data.text);
    bugReportForm.append('source', 'WEB');
    bugReportForm.append('device', device);

    attachments.forEach((file: File) => {
      bugReportForm.append('attachments[]', file);
    });

    dispatch(setActivity(true));
    reportBug(bugReportForm)
      .unwrap()
      .then(() => {
        showSuccess?.(t({ id: 'screens.settings.bug.report.success' }));
        form.resetFields();
        onClose();
      })
      .catch((err: ApiError) => {
        console.log(err);
        showError?.(t({ id: 'screens.settings.bug.report.request.error' }));
      })
      .finally(() => {
        dispatch(setActivity(false));
      })
  }

  return (
    <Drawer
      title={t({ id: 'screens.setting.setting.setting.nav.bugReport' })}
      open={open}
      closable
      onClose={onClose}
      width={560}
    >
      <Flex vertical align='center' gap={16}>
        <BugOutlined
          style={{
            fontSize: 36,
            padding: 18,
            width: 'fit-content',
            boxSizing: 'content-box',
            background: 'rgba(0, 0, 0, 0.04)',
            borderRadius: 100,
            marginBottom: 15,
          }}
        />
        <Form
          form={form}
          onFinish={(e) => onSubmit(e)}
          className='w100'
        >
          <Flex vertical align='center'>
            <Form.Item className='w100' name="subject" rules={formRules.subject}>
              <Input placeholder={t({ id: 'screens.settings.bug.report.subject.placeholder' })} />
            </Form.Item>
            <Form.Item className='w100' name="description" rules={formRules.description}>
              <Input.TextArea autoSize={{ minRows: 4 }} placeholder={t({ id: 'screens.settings.bug.report.placeholder' })} />
            </Form.Item>
            <Form.Item className='w100' name="attachments" >
              <Upload.Dragger {...props}>
                <p
                  className="ant-upload-text"
                  style={{ textAlign: 'left', fontWeight: 700 }}>
                  <u>{t({ id: 'screens.settings.bug.report.add.attachment' })}</u>
                </p>
                <p className="ant-upload-hint" style={{ textAlign: 'left' }}>
                  {t({ id: 'screens.settings.bug.report.add.attachment.description' })}
                </p>
              </Upload.Dragger>
            </Form.Item>
            <Button
              id="btn__account-name-submit"
              variant="solid"
              color='default'
              onClick={form.submit}>
              {t({ id: 'buttons.send' })}
            </Button>
          </Flex>
        </Form>
      </Flex>
    </Drawer>
  )
}

export default BugReportDrawer;